<script setup>
/* global axios */
// Lista de Projetos — 3 visualizações (cards / lista / kanban por status), busca +
// filtro de status (identidade Chatwoot), criar/editar/excluir.
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import KanbanSelect from './KanbanSelect.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const accountId = route.params.accountId;
const base = `/api/v1/accounts/${accountId}/kanban`;

const projects = ref([]);
const loading = ref(true);
const search = ref('');
const statusFilter = ref('all');
const view = ref(localStorage.getItem('kanbanProjectsView') || 'cards');
const showModal = ref(false);
const editing = ref(null);
const dragId = ref(null);
const form = ref({
  name: '',
  description: '',
  status: 'active',
  color: '#3b82f6',
  deadline: '',
});

const PALETTE = [
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#ec4899',
  '#06b6d4',
  '#64748b',
];
const STATUS = computed(() => ({
  active: {
    label: t('PROJECTS.status.active'),
    cls: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  },
  completed: {
    label: t('PROJECTS.status.completed'),
    cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  },
  cancelled: {
    label: t('PROJECTS.status.cancelled'),
    cls: 'bg-gray-100 text-gray-600 dark:bg-n-solid-3 dark:text-n-slate-11',
  },
}));
const statusOptions = computed(() => [
  { value: 'all', label: t('PROJECTS.list.allStatuses') },
  { value: 'active', label: t('PROJECTS.status.active') },
  { value: 'completed', label: t('PROJECTS.status.completed') },
  { value: 'cancelled', label: t('PROJECTS.status.cancelled') },
]);
const VIEWS = [
  { key: 'cards', label: 'PROJECTS.list.viewCards', icon: 'i-lucide-layout-grid' },
  { key: 'list', label: 'PROJECTS.list.viewList', icon: 'i-lucide-list' },
  {
    key: 'kanban',
    label: 'PROJECTS.list.viewKanban',
    icon: 'i-lucide-columns-3',
  },
];
const KANBAN_COLS = ['active', 'completed', 'cancelled'];

const load = async () => {
  loading.value = true;
  projects.value = (await axios.get(`${base}/projects`)).data.data || [];
  loading.value = false;
};
const filtered = computed(() => {
  const q = search.value.trim().toLowerCase();
  return projects.value.filter(p => {
    if (statusFilter.value !== 'all' && p.status !== statusFilter.value)
      return false;
    if (q) {
      const hay = `${p.name || ''} ${p.description || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
});
const byStatus = status => filtered.value.filter(p => p.status === status);

const setView = v => {
  view.value = v;
  localStorage.setItem('kanbanProjectsView', v);
};

const openNew = () => {
  editing.value = null;
  form.value = {
    name: '',
    description: '',
    status: 'active',
    color: '#3b82f6',
    deadline: '',
  };
  showModal.value = true;
};
const openEdit = p => {
  editing.value = p;
  form.value = {
    name: p.name,
    description: p.description || '',
    status: p.status,
    color: p.color || '#3b82f6',
    deadline: p.deadline ? p.deadline.slice(0, 10) : '',
  };
  showModal.value = true;
};
const save = async () => {
  const payload = { ...form.value };
  if (!payload.name.trim()) return;
  if (!payload.deadline) delete payload.deadline;
  if (editing.value)
    await axios.patch(`${base}/projects/${editing.value.id}`, payload);
  else await axios.post(`${base}/projects`, payload);
  showModal.value = false;
  await load();
};
const remove = async p => {
  if (!window.confirm(t('PROJECTS.list.confirmDelete', { name: p.name })))
    return;
  await axios.delete(`${base}/projects/${p.id}`);
  await load();
};
// kanban drag: solta numa coluna -> muda o status do projeto
const onDrop = async status => {
  const p = projects.value.find(x => x.id === dragId.value);
  dragId.value = null;
  if (!p || p.status === status) return;
  p.status = status;
  await axios.patch(`${base}/projects/${p.id}`, { status });
  await load();
};
const openDetail = p =>
  router.push(`/app/accounts/${accountId}/projects/${p.id}`);
const fmtDate = d => (d ? new Date(d).toLocaleDateString('pt-BR') : '—');

onMounted(load);
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <!-- header -->
    <div class="sticky top-0 z-10 bg-n-background border-b border-n-weak">
      <div class="flex items-center h-16 gap-3 px-6">
        <span class="i-lucide-folder-kanban size-6 text-n-slate-12 shrink-0" />
        <h1 class="text-heading-1 text-n-slate-12 shrink-0">
          {{ t('PROJECTS.title') }}
        </h1>
        <button
          class="ml-auto inline-flex items-center gap-1.5 px-3.5 h-9 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110 shrink-0"
          @click="openNew"
        >
          <span class="i-lucide-plus size-4" />
          {{ t('PROJECTS.list.newProjectButton') }}
        </button>
      </div>
      <!-- toolbar: busca + filtro + view -->
      <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 px-6 pb-3">
        <div
          class="flex items-center gap-2 flex-1 h-9 px-3 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-alpha-1 transition-all duration-200 focus-within:outline-n-blue-9 hover:outline-n-slate-6"
        >
          <span class="i-lucide-search size-4 shrink-0 text-n-slate-10" />
          <input
            v-model="search"
            type="text"
            :placeholder="t('PROJECTS.list.searchPlaceholder')"
            class="reset-base flex-1 min-w-0 h-full m-0 p-0 text-sm bg-transparent border-0 outline-none text-n-slate-12 placeholder:text-n-slate-10"
          />
        </div>
        <div class="w-full sm:w-52">
          <KanbanSelect v-model="statusFilter" :options="statusOptions" />
        </div>
        <!-- view toggle -->
        <div
          class="flex items-center h-9 p-0.5 rounded-lg bg-n-alpha-1 outline outline-1 -outline-offset-1 outline-n-weak shrink-0"
        >
          <button
            v-for="v in VIEWS"
            :key="v.key"
            class="inline-flex items-center gap-1.5 h-8 px-2.5 text-xs font-medium rounded-md transition-colors"
            :class="
              view === v.key
                ? 'bg-n-background text-n-slate-12 shadow-sm'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            :title="t(v.label)"
            @click="setView(v.key)"
          >
            <span :class="v.icon" class="size-4" />
            <span class="hidden md:inline">{{ t(v.label) }}</span>
          </button>
        </div>
      </div>
    </div>

    <div class="flex-1 overflow-auto">
      <div class="px-6 py-6">
        <div v-if="loading" class="text-n-slate-11">
          {{ t('PROJECTS.common.loadingShort') }}
        </div>
        <div
          v-else-if="!projects.length"
          class="py-10 text-center text-n-slate-10"
        >
          {{ t('PROJECTS.list.empty') }}
        </div>
        <div
          v-else-if="!filtered.length"
          class="py-10 text-center text-n-slate-10"
        >
          {{ t('PROJECTS.list.noMatch') }}
        </div>

        <!-- ===== CARDS ===== -->
        <div
          v-else-if="view === 'cards'"
          class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        >
          <div
            v-for="p in filtered"
            :key="p.id"
            class="p-3.5 rounded-xl border border-n-weak bg-n-solid-1 cursor-pointer hover:shadow-md transition-shadow"
            @click="openDetail(p)"
          >
            <div class="flex items-start gap-2">
              <span
                class="w-3 h-3 mt-1 rounded-full shrink-0"
                :style="{ background: p.color || '#94a3b8' }"
              />
              <div class="flex-1 min-w-0">
                <h3 class="text-heading-3 truncate text-n-slate-12">
                  {{ p.name }}
                </h3>
                <p
                  v-if="p.description"
                  class="text-xs text-n-slate-10 line-clamp-2"
                >
                  {{ p.description }}
                </p>
              </div>
              <span
                class="px-2 py-0.5 text-[0.625rem] rounded-full shrink-0"
                :class="STATUS[p.status]?.cls"
                >{{ STATUS[p.status]?.label }}</span
              >
            </div>
            <div class="mt-3">
              <div
                class="flex justify-between mb-1 text-[0.6875rem] text-n-slate-10"
              >
                <span>{{ t('PROJECTS.list.progress') }}</span
                ><span>{{ p.progress }}%</span>
              </div>
              <div class="h-1.5 rounded-full bg-n-alpha-2">
                <div
                  class="h-1.5 rounded-full bg-n-brand"
                  :style="{ width: p.progress + '%' }"
                />
              </div>
            </div>
            <div
              class="flex items-center gap-3 mt-3 text-[0.6875rem] text-n-slate-10"
            >
              <span class="inline-flex items-center gap-1">
                <span class="i-lucide-check-square size-3.5" />{{
                  p.task_count
                }}</span
              >
              <span class="inline-flex items-center gap-1">
                <span class="i-lucide-users size-3.5" />{{ p.member_count }}</span
              >
              <span class="inline-flex items-center gap-1 ml-auto">
                <span class="i-lucide-calendar size-3.5" />{{
                  fmtDate(p.deadline)
                }}</span
              >
            </div>
            <div class="flex gap-1 mt-3" @click.stop>
              <button
                class="flex-1 inline-flex items-center justify-center gap-1.5 px-3 h-8 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
                @click="openDetail(p)"
              >
                {{ t('PROJECTS.common.open') }}
              </button>
              <button
                class="inline-flex items-center justify-center size-8 rounded-md text-n-slate-10 hover:bg-n-alpha-1"
                :title="t('PROJECTS.list.editTitle')"
                @click="openEdit(p)"
              >
                <span class="i-lucide-pencil size-4" />
              </button>
              <button
                class="inline-flex items-center justify-center size-8 rounded-md text-n-ruby-11 hover:bg-n-alpha-1"
                :title="t('PROJECTS.list.removeTitle')"
                @click="remove(p)"
              >
                <span class="i-lucide-trash-2 size-4" />
              </button>
            </div>
          </div>
        </div>

        <!-- ===== LISTA ===== -->
        <table
          v-else-if="view === 'list'"
          class="w-full overflow-hidden text-sm bg-n-background border rounded-lg border-n-weak"
        >
          <thead class="text-left bg-n-alpha-1">
            <tr class="text-[0.6875rem] uppercase text-n-slate-10">
              <th class="px-4 py-3">{{ t('PROJECTS.list.colName') }}</th>
              <th class="px-4 py-3">{{ t('PROJECTS.list.colStatus') }}</th>
              <th class="px-4 py-3 w-40">
                {{ t('PROJECTS.list.colProgress') }}
              </th>
              <th class="px-4 py-3">{{ t('PROJECTS.list.colTasks') }}</th>
              <th class="px-4 py-3">{{ t('PROJECTS.list.colMembers') }}</th>
              <th class="px-4 py-3">{{ t('PROJECTS.list.colDeadline') }}</th>
              <th class="px-4 py-3 text-right">
                {{ t('PROJECTS.list.colActions') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="p in filtered"
              :key="p.id"
              class="border-t border-n-weak cursor-pointer hover:bg-n-alpha-1"
              @click="openDetail(p)"
            >
              <td class="px-4 py-3">
                <div class="flex items-center gap-2">
                  <span
                    class="w-2.5 h-2.5 rounded-full shrink-0"
                    :style="{ background: p.color || '#94a3b8' }"
                  />
                  <span class="font-medium text-n-slate-12 truncate">{{
                    p.name
                  }}</span>
                </div>
              </td>
              <td class="px-4 py-3">
                <span
                  class="px-2 py-0.5 text-[0.625rem] rounded-full"
                  :class="STATUS[p.status]?.cls"
                  >{{ STATUS[p.status]?.label }}</span
                >
              </td>
              <td class="px-4 py-3">
                <div class="flex items-center gap-2">
                  <div class="flex-1 h-1.5 rounded-full bg-n-alpha-2">
                    <div
                      class="h-1.5 rounded-full bg-n-brand"
                      :style="{ width: p.progress + '%' }"
                    />
                  </div>
                  <span class="text-[0.6875rem] text-n-slate-10 w-8"
                    >{{ p.progress }}%</span
                  >
                </div>
              </td>
              <td class="px-4 py-3 text-n-slate-11">{{ p.task_count }}</td>
              <td class="px-4 py-3 text-n-slate-11">{{ p.member_count }}</td>
              <td class="px-4 py-3 text-n-slate-10">
                {{ fmtDate(p.deadline) }}
              </td>
              <td class="px-4 py-3 text-right whitespace-nowrap" @click.stop>
                <button
                  class="inline-flex items-center justify-center size-8 rounded-md text-n-slate-10 hover:bg-n-alpha-2"
                  :title="t('PROJECTS.list.editTitle')"
                  @click="openEdit(p)"
                >
                  <span class="i-lucide-pencil size-4" />
                </button>
                <button
                  class="inline-flex items-center justify-center size-8 rounded-md text-n-ruby-11 hover:bg-n-alpha-2"
                  :title="t('PROJECTS.list.removeTitle')"
                  @click="remove(p)"
                >
                  <span class="i-lucide-trash-2 size-4" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- ===== KANBAN ===== -->
        <div v-else class="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div
            v-for="col in KANBAN_COLS"
            :key="col"
            class="flex flex-col rounded-xl bg-n-alpha-1 border border-n-weak"
            @dragover.prevent
            @drop="onDrop(col)"
          >
            <div
              class="flex items-center gap-2 px-3 py-2.5 border-b border-n-weak"
            >
              <span
                class="px-2 py-0.5 text-[0.625rem] rounded-full"
                :class="STATUS[col]?.cls"
                >{{ STATUS[col]?.label }}</span
              >
              <span class="text-xs text-n-slate-10"
                >{{ byStatus(col).length }}</span
              >
            </div>
            <div class="flex flex-col gap-2 p-2.5 min-h-[120px]">
              <div
                v-for="p in byStatus(col)"
                :key="p.id"
                draggable="true"
                class="p-3 rounded-lg border border-n-weak bg-n-background cursor-pointer hover:shadow-sm"
                @dragstart="dragId = p.id"
                @click="openDetail(p)"
              >
                <div class="flex items-start gap-2">
                  <span
                    class="w-2.5 h-2.5 mt-1 rounded-full shrink-0"
                    :style="{ background: p.color || '#94a3b8' }"
                  />
                  <h3 class="flex-1 text-sm font-medium truncate text-n-slate-12">
                    {{ p.name }}
                  </h3>
                </div>
                <div class="mt-2 h-1.5 rounded-full bg-n-alpha-2">
                  <div
                    class="h-1.5 rounded-full bg-n-brand"
                    :style="{ width: p.progress + '%' }"
                  />
                </div>
                <div
                  class="flex items-center gap-3 mt-2 text-[0.625rem] text-n-slate-10"
                >
                  <span class="inline-flex items-center gap-1"
                    ><span class="i-lucide-check-square size-3" />{{
                      p.task_count
                    }}</span
                  >
                  <span class="inline-flex items-center gap-1"
                    ><span class="i-lucide-users size-3" />{{
                      p.member_count
                    }}</span
                  >
                  <span class="ml-auto">{{ fmtDate(p.deadline) }}</span>
                </div>
              </div>
              <p
                v-if="!byStatus(col).length"
                class="py-4 text-center text-[0.6875rem] text-n-slate-9"
              >
                —
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- modal criar/editar -->
    <div
      v-if="showModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-alpha-black2"
      @click.self="showModal = false"
    >
      <div
        class="bg-n-background rounded-xl shadow-xl w-[440px] max-w-[92vw] p-5"
      >
        <h3 class="text-heading-3 mb-3 text-n-slate-12">
          {{
            editing ? t('PROJECTS.modal.editTitle') : t('PROJECTS.newProject')
          }}
        </h3>
        <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
          t('PROJECTS.list.modalNameLabel')
        }}</label>
        <input
          v-model="form.name"
          class="w-full mb-2 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          :placeholder="t('PROJECTS.list.modalNamePlaceholder')"
          @keyup.enter="save"
        />
        <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
          t('PROJECTS.modal.descriptionLabel')
        }}</label>
        <textarea
          v-model="form.description"
          rows="2"
          class="w-full mb-2 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          :placeholder="t('PROJECTS.list.modalDescriptionPlaceholder')"
        />
        <div class="flex gap-3 mb-2">
          <div class="flex-1">
            <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
              t('PROJECTS.modal.statusLabel')
            }}</label>
            <select
              v-model="form.status"
              class="w-full pl-3 pr-9 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            >
              <option value="active">{{ t('PROJECTS.status.active') }}</option>
              <option value="completed">
                {{ t('PROJECTS.status.completed') }}
              </option>
              <option value="cancelled">
                {{ t('PROJECTS.status.cancelled') }}
              </option>
            </select>
          </div>
          <div class="flex-1">
            <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
              t('PROJECTS.modal.deadlineLabel')
            }}</label>
            <input
              v-model="form.deadline"
              type="date"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            />
          </div>
        </div>
        <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
          t('PROJECTS.list.modalColorLabel')
        }}</label>
        <div class="flex gap-2 mb-4">
          <button
            v-for="c in PALETTE"
            :key="c"
            class="w-6 h-6 rounded-full outline-offset-2"
            :style="{
              background: c,
              outline: form.color === c ? '2px solid #0f172a' : 'none',
            }"
            @click="form.color = c"
          />
        </div>
        <div class="flex justify-end gap-2">
          <button
            class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
            @click="showModal = false"
          >
            {{ t('PROJECTS.modal.cancel') }}
          </button>
          <button
            class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110"
            @click="save"
          >
            {{
              editing ? t('PROJECTS.common.save') : t('PROJECTS.common.create')
            }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
